import { useEffect, useState } from "react";
import { Tooltip, Box } from "@mui/material";
import { MapProtectedRainforestArea } from "types/map";

type Props = {
  areas: Array<MapProtectedRainforestArea>;
  map: google.maps.Map | null;
  highlight?: boolean;
  onToggleHumans?: () => void;
};

type TooltipState = {
  x: number;
  y: number;
  text: string;
};

const MapGeoProtectedHumans = ({
  map,
  areas,
  highlight,
  onToggleHumans,
}: Props) => {
  const [dataLayer, setDataLayer] = useState<google.maps.Data | null>(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);

  useEffect(() => {
    if (!map) return;

    // Create a dedicated data layer for humans
    const layer = new google.maps.Data({ map });
    setDataLayer(layer);

    return () => {
      layer.setMap(null); // Clean up on unmount
    };
  }, [map]);

  useEffect(() => {
    if (!dataLayer || !areas.length) return;

    // Clear existing data in this layer
    dataLayer.forEach((feature) => {
      dataLayer.remove(feature);
    });

    // Add new GEOJSON data
    areas.forEach((area) => {
      try {
        const features = dataLayer.addGeoJson(area.geoJSON);
        features.forEach((feature) => {
          feature.setProperty("strokeColor", area.areaStrokeColor);
          feature.setProperty("fillColor", area.areaFillColor);
        });
      } catch (e) {
        console.error(
          "[Map] MapGeoProtectedHumans Failed to add GeoJSON to map",
          e
        );
      }
    });

    // Event listeners for hover effect
    const mouseOverListener = dataLayer.addListener(
      "mouseover",
      (event: google.maps.Data.MouseEvent) => {
        dataLayer.revertStyle();
        event.feature.setProperty("isHovered", true);
        setTooltip({
          x: (event.domEvent as MouseEvent).clientX,
          y: (event.domEvent as MouseEvent).clientY,
          text: "Protected indigenous communities",
        });
      }
    );

    const mouseMoveListener = dataLayer.addListener(
      "mousemove",
      (event: google.maps.Data.MouseEvent) => {
        setTooltip({
          x: (event.domEvent as MouseEvent).clientX,
          y: (event.domEvent as MouseEvent).clientY,
          text: "Protected indigenous communities",
        });
      }
    );

    const mouseOutListener = dataLayer.addListener(
      "mouseout",
      (event: google.maps.Data.MouseEvent) => {
        event.feature.setProperty("isHovered", false);
        setTooltip(null);
      }
    );

    const clickListener = dataLayer.addListener("click", () => {
      if (onToggleHumans) {
        onToggleHumans();
      }
    });

    // Style the features
    dataLayer.setStyle((feature) => {
      const isHovered = feature.getProperty("isHovered");
      return {
        fillColor: feature.getProperty("fillColor") as string,
        strokeColor: feature.getProperty("strokeColor") as string,
        strokeWeight: isHovered || highlight ? 2 : 0,
        fillOpacity: isHovered || highlight ? 0.4 : 0.2,
        zIndex: 100, // Ensure humans are on top
        clickable: true,
        cursor: "pointer",
      };
    });

    return () => {
      google.maps.event.removeListener(mouseOverListener);
      google.maps.event.removeListener(mouseMoveListener);
      google.maps.event.removeListener(mouseOutListener);
      google.maps.event.removeListener(clickListener);
    };
  }, [dataLayer, areas, highlight, onToggleHumans]);

  return (
    <>
      {tooltip && (
        <Tooltip
          title={tooltip.text}
          open={true}
          arrow
          placement="top"
          PopperProps={{
            sx: {
              pointerEvents: "none",
            },
          }}
        >
          <Box
            sx={{
              position: "fixed",
              top: tooltip.y,
              left: tooltip.x,
              visibility: "hidden",
              pointerEvents: "none",
            }}
          />
        </Tooltip>
      )}
    </>
  );
};

export default MapGeoProtectedHumans;
